import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  User,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  getIdToken,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  ActionCodeSettings,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase';

const EMAIL_LINK_STORAGE_KEY = 'rankify.emailForSignIn';

function buildActionCodeSettings(): ActionCodeSettings {
  // The link must redirect back to the app's current origin so the auth handler
  // (HashRouter) sees it. Firebase requires the domain be in the project's
  // authorized domains list (Firebase Console -> Authentication -> Settings).
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return {
    url: `${origin}/`,
    handleCodeInApp: true,
  };
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  signInWithGoogle: () => Promise<void>;
  sendEmailLink: (email: string) => Promise<void>;
  emailLinkSent: boolean;
  resetEmailLinkSent: () => void;
  signInWithPassword: (email: string, password: string) => Promise<boolean>;
  signUpWithPassword: (email: string, password: string) => Promise<boolean>;
  sendPasswordReset: (email: string) => Promise<boolean>;
  passwordResetSent: boolean;
  resetPasswordResetSent: () => void;
  clearError: () => void;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [emailLinkSent, setEmailLinkSent] = useState(false);
  const [passwordResetSent, setPasswordResetSent] = useState(false);

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Complete sign-in if user landed on the app via an email link
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const href = window.location.href;
    if (!isSignInWithEmailLink(auth, href)) return;
    let email = window.localStorage.getItem(EMAIL_LINK_STORAGE_KEY) || '';
    if (!email) {
      // Different device/browser than where the link was requested.
      // eslint-disable-next-line no-alert
      email = window.prompt('Confirm your email to finish signing in') || '';
    }
    if (!email) return;
    setLoading(true);
    signInWithEmailLink(auth, email, href)
      .then(() => {
        window.localStorage.removeItem(EMAIL_LINK_STORAGE_KEY);
        // Strip the email-link query params so a refresh doesn't retry.
        const cleanUrl = `${window.location.origin}${window.location.pathname}`;
        window.history.replaceState(null, '', cleanUrl);
        window.location.hash = '/';
      })
      .catch((err) => {
        console.error('Email-link sign-in error:', err);
        setError(err?.message || 'Failed to complete email sign-in');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const signInWithGoogle = useCallback(async () => {
    try {
      setError(null);
      await signInWithPopup(auth, googleProvider);
      // Reset to the home route so onboarding gates run consistently after login.
      if (typeof window !== 'undefined') {
        window.location.hash = '/';
      }
    } catch (err: any) {
      console.error('Google sign-in error:', err);
      setError(err.message || 'Failed to sign in with Google');
    }
  }, []);

  const sendEmailLink = useCallback(async (email: string) => {
    try {
      setError(null);
      const trimmed = email.trim();
      if (!trimmed) {
        setError('Please enter your email address.');
        return;
      }
      await sendSignInLinkToEmail(auth, trimmed, buildActionCodeSettings());
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(EMAIL_LINK_STORAGE_KEY, trimmed);
      }
      setEmailLinkSent(true);
    } catch (err: any) {
      console.error('sendSignInLinkToEmail error:', err);
      setError(err?.message || 'Failed to send sign-in link');
    }
  }, []);

  const resetEmailLinkSent = useCallback(() => {
    setEmailLinkSent(false);
  }, []);

  const mapAuthError = (err: any): string => {
    const code = err?.code || '';
    switch (code) {
      case 'auth/invalid-email':
        return 'That email address looks invalid.';
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'Email or password is incorrect.';
      case 'auth/email-already-in-use':
        return 'An account with this email already exists. Try signing in instead.';
      case 'auth/weak-password':
        return 'Password must be at least 6 characters.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please wait a moment and try again.';
      default:
        return err?.message || 'Authentication failed';
    }
  };

  const signInWithPassword = useCallback(async (email: string, password: string): Promise<boolean> => {
    try {
      setError(null);
      await signInWithEmailAndPassword(auth, email.trim(), password);
      if (typeof window !== 'undefined') {
        window.location.hash = '/';
      }
      return true;
    } catch (err: any) {
      console.error('Password sign-in error:', err);
      setError(mapAuthError(err));
      return false;
    }
  }, []);

  const signUpWithPassword = useCallback(async (email: string, password: string): Promise<boolean> => {
    try {
      setError(null);
      await createUserWithEmailAndPassword(auth, email.trim(), password);
      if (typeof window !== 'undefined') {
        window.location.hash = '/';
      }
      return true;
    } catch (err: any) {
      console.error('Password sign-up error:', err);
      setError(mapAuthError(err));
      return false;
    }
  }, []);

  const sendPasswordReset = useCallback(async (email: string): Promise<boolean> => {
    try {
      setError(null);
      const trimmed = email.trim();
      if (!trimmed) {
        setError('Please enter your email address.');
        return false;
      }
      await sendPasswordResetEmail(auth, trimmed);
      setPasswordResetSent(true);
      return true;
    } catch (err: any) {
      console.error('sendPasswordResetEmail error:', err);
      setError(mapAuthError(err));
      return false;
    }
  }, []);

  const resetPasswordResetSent = useCallback(() => {
    setPasswordResetSent(false);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const logout = useCallback(async () => {
    try {
      setError(null);
      await signOut(auth);
    } catch (err: any) {
      console.error('Logout error:', err);
      setError(err.message || 'Failed to sign out');
    }
  }, []);

  const getToken = useCallback(async (): Promise<string | null> => {
    if (!user) return null;
    try {
      return await getIdToken(user);
    } catch (err) {
      console.error('Failed to get token:', err);
      return null;
    }
  }, [user]);

  const value: AuthContextType = {
    user,
    loading,
    error,
    signInWithGoogle,
    sendEmailLink,
    emailLinkSent,
    resetEmailLinkSent,
    signInWithPassword,
    signUpWithPassword,
    sendPasswordReset,
    passwordResetSent,
    resetPasswordResetSent,
    clearError,
    logout,
    getToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
